import React, { useEffect, useState, useRef } from 'react';
import { ArrowRightCircleIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { Norden } from './Norden';
import { motion } from 'framer-motion';
import { clientCases } from '../data/allClientCases';
export const ClientCases: React.FC = () => {
  const [cardsPerView, setCardsPerView] = useState(3);
  const [gap, setGap] = useState(32);
  const [isMobile, setIsMobile] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [cardWidth, setCardWidth] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const mobileScrollRef = useRef<HTMLDivElement>(null);
  const scrollTimeoutRef = useRef<number | null>(null);
  const maxIndex = Math.max(0, clientCases.length - cardsPerView);
  useEffect(() => {
    const updateLayout = () => {
      const w = window.innerWidth;
      let perView = 3;
      let g = 32;
      let mobile = false;
      if (w < 768) {
        perView = 1;
        g = 16;
        mobile = true;
      } else if (w < 1024) {
        perView = 2;
        g = 32;
      } else {
        perView = 3;
        g = 32;
      }
      setCardsPerView(perView);
      setGap(g);
      setIsMobile(mobile);
      setCurrentIndex(prev => Math.min(prev, Math.max(0, clientCases.length - perView)));
      if (containerRef.current) {
        const containerWidth = containerRef.current.offsetWidth;
        // On mobile: shrink cards so the next card peeks in from the right (foreshadow swipe)
        const width = mobile ? containerWidth * 0.85 : (containerWidth - g * (perView - 1)) / perView;
        setCardWidth(width);
      }
    };
    updateLayout();
    window.addEventListener('resize', updateLayout);
    return () => window.removeEventListener('resize', updateLayout);
  }, []);
  const handlePrev = () => {
    setCurrentIndex(prev => Math.max(0, prev - 1));
  };
  const handleNext = () => {
    setCurrentIndex(prev => Math.min(maxIndex, prev + 1));
  };
  // Sync currentIndex with native scroll position on mobile (debounced)
  const handleMobileScroll = () => {
    if (!mobileScrollRef.current || cardWidth === 0) return;
    if (scrollTimeoutRef.current) {
      window.clearTimeout(scrollTimeoutRef.current);
    }
    scrollTimeoutRef.current = window.setTimeout(() => {
      const el = mobileScrollRef.current;
      if (!el) return;
      const step = cardWidth + gap;
      const index = Math.round(el.scrollLeft / step);
      setCurrentIndex(Math.max(0, Math.min(clientCases.length - 1, index)));
    }, 80);
  };
  // When user taps a dot on mobile, programmatically scroll to that card
  const goToIndex = (i: number) => {
    setCurrentIndex(i);
    if (isMobile && mobileScrollRef.current && cardWidth > 0) {
      mobileScrollRef.current.scrollTo({
        left: i * (cardWidth + gap),
        behavior: 'smooth'
      });
    }
  };
  return <div id="client-cases" className="w-full bg-gradient-to-br from-[#F7F8FA] to-[#E8F0FE] py-16 md:py-[60px] relative overflow-hidden scroll-mt-24" data-id="element-141" data-mp="26w31hzwk4rbg">
      {/* Subtle background pattern */}
      <div className="absolute inset-0 opacity-5" data-id="element-142" data-mp="qevyyw0xp1c1">
        <div className="w-full h-full bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0iIzAwN0FGRiIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')]" data-id="element-143" data-mp="qfnkfe0thiol"></div>
      </div>

      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px] relative z-10" data-id="element-144" data-mp="1e69oku7paitb">
        <div className="max-w-3xl mx-auto text-center mb-16" data-id="element-145" data-mp="1j2rtpv6y0j66">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="font-bold text-[#111827] mb-4 text-3xl md:text-4xl" data-id="element-146" data-mp="3fwl7ijr46jsv">
            Klantcases
          </motion.h2>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} className="text-[#6B7280] mb-8 leading-relaxed text-lg" data-id="element-147" data-mp="2zjfiomn8iqr1">
            Van fysiotherapiepraktijk tot horeca-uitzendbureau, van zwemclub tot
            schoonmaakbedrijf. Deze cases laten zien hoe ondernemers hun online
            zichtbaarheid vergroten en meer aanvragen binnenhalen. Van betere
            vindbaarheid tot meer contactmomenten: ontdek wat er mogelijk is.
          </motion.p>
          <motion.div initial={{
          opacity: 0,
          scale: 0.8
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.2,
          type: 'spring'
        }} className="relative inline-block" data-id="element-148" data-mp="1j4e5r1ntc4jf">
            <Norden variant="sticker" className="opacity-80" data-id="element-149" data-mp="3ptpp2ydawax0" />
          </motion.div>
        </div>

        {/* Carousel */}
        <div className="relative" ref={containerRef} data-id="element-150" data-mp="mcpiy32ymabw">
          {/* Navigation Buttons — hidden on mobile, swipe-only there */}
          <button onClick={handlePrev} disabled={currentIndex === 0} className="hidden md:flex absolute -left-5 top-1/2 -translate-y-1/2 z-20 w-12 h-12 bg-white rounded-full shadow-lg items-center justify-center text-[#007AFF] hover:bg-[#007AFF] hover:text-white transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:text-[#007AFF]" aria-label="Vorige cases" data-id="element-151" data-mp="8dpvuzv3i3hu">
            <ChevronLeftIcon className="w-6 h-6" data-id="element-152" data-mp="yn8iqz4u9gdw" />
          </button>
          <button onClick={handleNext} disabled={currentIndex >= maxIndex} className="hidden md:flex absolute -right-5 top-1/2 -translate-y-1/2 z-20 w-12 h-12 bg-white rounded-full shadow-lg items-center justify-center text-[#007AFF] hover:bg-[#007AFF] hover:text-white transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:text-[#007AFF]" aria-label="Volgende cases" data-id="element-153" data-mp="yp1p0dbjhyld">
            <ChevronRightIcon className="w-6 h-6" data-id="element-154" data-mp="axmis9gn6x4u" />
          </button>

          {/* MOBILE: native scroll-snap carousel — smooth momentum + one card at a time */}
          {isMobile ? <div ref={mobileScrollRef} onScroll={handleMobileScroll} className="flex items-stretch gap-4 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-2 -mx-4 px-4 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden" style={{
          WebkitOverflowScrolling: 'touch'
        }} data-id="element-155" data-mp="25avmfkmibof6">
              {clientCases.map((clientCase, index) => <div key={index} className="flex-shrink-0 snap-start bg-white rounded-2xl shadow-[0_8px_24px_rgba(0,122,255,0.08)] overflow-hidden flex flex-col" style={{
            width: cardWidth > 0 ? cardWidth : '85%'
          }} data-id="element-156" data-mp="2ml9825tb0tvd" data-mpi={index}>
                  {/* Image */}
                  <div className="relative h-56 flex-shrink-0 overflow-hidden" data-id="element-157" data-mp="1yx86jgujmdpg">
                    <img src={clientCase.heroImage} alt={`${clientCase.clientName}, klantcase Noord Digital`} className="w-full h-full object-cover object-top" loading="lazy" draggable={false} data-id="element-158" data-mp="23vt418ieuja7" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" data-id="element-159" data-mp="45p3o1eoq57a"></div>
                    <div className="absolute bottom-4 left-4 right-4" data-id="element-160" data-mp="dxl5nnjw6fgs">
                      <span className="inline-block bg-[#007AFF] text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-sm" data-id="element-161" data-mp="dzxw6p3pty5g">
                        {clientCase.sector}
                      </span>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6 flex flex-col flex-grow" data-id="element-162" data-mp="iwobvbblyrqc">
                    <h3 className="text-xl font-bold text-[#111827] mb-3 min-h-[3.5rem]" data-id="element-163" data-mp="wvnyyejh2yke">
                      {clientCase.clientName}
                    </h3>
                    <p className="text-[#6B7280] mb-6 leading-relaxed flex-grow" data-id="element-164" data-mp="2yo3st5z7cytq">
                      {clientCase.shortDescription}
                    </p>
                    <a href={`/cases/${clientCase.slug}`} className="inline-flex items-center text-[#007AFF] font-semibold hover:text-[#005FCC] transition-colors mt-auto w-fit" data-id="element-165" data-mp="2tqk236lbitkw">
                      Lees het verhaal
                      <ArrowRightCircleIcon className="h-5 w-5 ml-2" data-id="element-166" data-mp="10dlic1570a0q" />
                    </a>
                  </div>
                </div>)}
            </div> /* DESKTOP: animated translate carousel driven by arrows / dots */ : <div className="overflow-hidden" data-id="element-167" data-mp="vfjxbao7i0mm">
              <motion.div className="flex items-stretch gap-8" animate={{
            x: cardWidth > 0 ? -(currentIndex * (cardWidth + gap)) : 0
          }} transition={{
            type: 'spring',
            stiffness: 300,
            damping: 30
          }} data-id="element-168" data-mp="vglc9q8cf79m">
                {clientCases.map((clientCase, index) => <motion.div key={index} className="flex-shrink-0 bg-white rounded-2xl shadow-[0_8px_24px_rgba(0,122,255,0.08)] overflow-hidden transition-shadow duration-300 hover:shadow-[0_12px_32px_rgba(0,122,255,0.15)] group flex flex-col" style={{
              width: cardWidth > 0 ? cardWidth : '100%'
            }} initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '-50px'
            }} transition={{
              duration: 0.4,
              delay: index * 0.05
            }} data-id="element-169" data-mp="2fultctlo9m7m" data-mpi={index}>
                    {/* Image */}
                    <div className="relative h-56 flex-shrink-0 overflow-hidden" data-id="element-170" data-mp="29w4ma70mdb8y">
                      <img src={clientCase.heroImage} alt={`${clientCase.clientName}, klantcase Noord Digital`} className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-110" loading="lazy" draggable={false} data-id="element-171" data-mp="22bu30cmu2w3r" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" data-id="element-172" data-mp="1jlm2816lwhqq"></div>
                      <div className="absolute bottom-4 left-4 right-4" data-id="element-173" data-mp="1enu24jvt5pbg">
                        <span className="inline-block bg-[#007AFF] text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-sm" data-id="element-174" data-mp="1tbf60g0ibskf">
                          {clientCase.sector}
                        </span>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="p-6 flex flex-col flex-grow" data-id="element-175" data-mp="vwjc04xwpgkm">
                      <h3 className="text-xl font-bold text-[#111827] mb-3 min-h-[3.5rem]" data-id="element-176" data-mp="vu6zw8ety0fh">
                        {clientCase.clientName}
                      </h3>
                      <p className="text-[#6B7280] mb-6 leading-relaxed flex-grow" data-id="element-177" data-mp="1pkpjlpw2h6nx">
                        {clientCase.shortDescription}
                      </p>
                      <a href={`/cases/${clientCase.slug}`} target="_self" className="inline-flex items-center text-[#007AFF] font-semibold hover:text-[#005FCC] transition-colors group/link mt-auto w-fit" data-id="element-178" data-mp="1zbk5gt5ei48u">
                        Lees het verhaal
                        <ArrowRightCircleIcon className="h-5 w-5 ml-2 transition-transform group-hover/link:translate-x-1" data-id="element-179" data-mp="1bko69aym86rk" />
                      </a>
                    </div>
                  </motion.div>)}
              </motion.div>
            </div>}

          {/* Dot indicators */}
          <div className="flex justify-center gap-2 mt-8" data-id="element-180" data-mp="2ryo48gdwhmc6">
            {Array.from({
            length: isMobile ? clientCases.length : maxIndex + 1
          }).map((_, i) => <button key={i} onClick={() => goToIndex(i)} className={`h-2.5 rounded-full transition-all duration-200 ${i === currentIndex ? 'bg-[#007AFF] w-8' : 'bg-[#007AFF]/20 hover:bg-[#007AFF]/40 w-2.5'}`} aria-label={`Ga naar slide ${i + 1}`} data-id="element-181" data-mp="bekqtpu1hb8k" data-mpi={i} />)}
          </div>
        </div>
      </div>
    </div>;
};